// Employee/components/View/WeekViewTable.tsx
import React, {useEffect, useState} from "react";
import {Table, TableBody, TableCell, TableHead, TableRow, Typography,} from "@mui/material";
import {SxProps, Theme} from "@mui/material/styles";
import {styles} from "../../styles/WeekViewStyles";
import {Event, EventAddData} from "../../types";
import {TimeSlotCell} from "./TimeSlotCell";
import {useTheme} from "../../../Theme/ThemeContext";
import {
    addDays,
    addHours,
    addMinutes,
    endOfDay,
    format,
    isAfter,
    isBefore,
    isEqual,
    isSameDay,
    isSameHour,
    isWithinInterval,
    parseISO,
    startOfDay
} from "date-fns";
import {fromZonedTime,} from "date-fns-tz";
import {ru} from "date-fns/locale";
import {Moment} from "moment";

interface WeekViewTableProps {
    events: Event[];
    currentWeekStart: Date;
    userType: "client" | "psychologist" | "observer";
    canEdit: boolean;
    onEdit: (event: Event | null) => void;
    onAdd: (eventData: EventAddData) => void;
    onRemove: (event: Event) => void;
    onCreateAvailability: (start: Date, end: Date) => void;
    showHorizontalLines: boolean;
    showVerticalLines: boolean;
    maxDaysForSelection: number;
    onConfirmSlot?: (slot: Moment) => void;
    timeZone: string;
}

interface TimeSlotSelection {
    firstClickedSlot: Date | null;
    availableSlots: Date[];
}

interface TimeInterval {
    start: Date;
    end: Date;
}

// Generates time slots (24 hours)
const generateTimeSlots = (): string[] => {
    return Array.from({length: 24}, (_, i) => {
        return `${String(i).padStart(2, "0")}:00`;
    });
};

// Get the Russian weekday abbreviation
const getRussianWeekdayAbbreviation = (date: Date): string => {
    const days = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
    const dayIndex = date.getDay();
    const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1;
    return days[adjustedIndex];
};

// Get styles for a specific cell
const getCellStyles = (
    currentDay: string,
    time: string,
    isLastRow: boolean,
    events: Event[],
    showHorizontalLines: boolean
): SxProps<Theme> => {
    const formattedTime = time.padStart(5, "0");
    const timeSlotStart = parseISO(`${currentDay}T${formattedTime}:00`);
    const timeSlotEnd = addHours(timeSlotStart, 1);

    // Check for overlapping events in the same hour
    const eventsInThisHour = events.filter((event) => {
        const eventStart = parseISO(event.start);
        const eventEnd = parseISO(event.end);
        return isWithinInterval(timeSlotStart, {
            start: eventStart,
            end: eventEnd,
        }) || isWithinInterval(addMinutes(timeSlotStart, 59), {
            start: eventStart,
            end: eventEnd,
        });
    });

    const hasOverlappingEvents = eventsInThisHour.length > 1;

    const hasEvents = eventsInThisHour.length > 0;

    const isEventStart = events.some((event) => {
        const eventStart = parseISO(event.start);
        return isSameHour(eventStart, timeSlotStart);
    });

    const isEventEnd = events.some((event) => {
        const eventEnd = parseISO(event.end);
        return isSameHour(eventEnd, timeSlotStart);
    });

    const hasEventContinuing = events.some((event) => {
        const eventStart = parseISO(event.start);
        const eventEnd = parseISO(event.end);
        return isWithinInterval(timeSlotStart, {start: eventStart, end: eventEnd}) &&
            isAfter(eventEnd, timeSlotEnd);
    });

    const hasEventEndingNextHour = events.some((event) => {
        const eventEnd = parseISO(event.end);
        return isSameHour(eventEnd, timeSlotEnd);
    });

    const isEventSpanning = events.some((event) => {
        const eventStart = parseISO(event.start);
        const eventEnd = parseISO(event.end);
        const eventDuration =
            (eventEnd.getTime() - eventStart.getTime()) / (1000 * 60 * 60);
        return (
            isSameHour(eventStart, timeSlotStart) &&
            isSameHour(eventEnd, addHours(timeSlotStart, 1)) &&
            eventDuration > 1
        );
    });

    const isNextEvent = events.some((event) => {
        const eventStart = parseISO(event.start);
        return isSameHour(eventStart, timeSlotEnd);
    });


    return {
        ...styles.dayColumn,
        borderBottom:
            hasEvents &&
            !isEventSpanning &&
            !isNextEvent &&
            !isSameHour(timeSlotEnd, parseISO(`${currentDay}T00:00`))
                ? (hasEventEndingNextHour && !hasEventContinuing)
                    ? "1px solid #444"
                    : "none"
                : isLastRow
                    ? styles.dayColumn.border
                    : "none",
        borderRight: "none",
        borderLeft: styles.dayColumn.border,
        borderTop: showHorizontalLines
            ? hasEvents
                ? isEventStart && !hasOverlappingEvents
                    ? "1px solid #444"
                    : isSameHour(timeSlotStart, parseISO(`${currentDay}T00:00`))
                        ? "1px solid #444"
                        : "none"
                : styles.dayColumn.border
            : "none",
        position: "relative",
        padding: 0,
        height: "auto",
    } as SxProps<Theme>;
};

export const WeekViewTable: React.FC<WeekViewTableProps> = ({
                                                                events,
                                                                currentWeekStart,
                                                                userType,
                                                                onEdit,
                                                                onAdd,
                                                                canEdit,
                                                                onRemove,
                                                                onCreateAvailability,
                                                                showHorizontalLines,
                                                                showVerticalLines,
                                                                maxDaysForSelection,
                                                                onConfirmSlot,
                                                                timeZone,
                                                            }) => {
    const [selection, setSelection] = useState<TimeSlotSelection>({
        firstClickedSlot: null,
        availableSlots: [],
    });
    const [isMobile, setIsMobile] = useState(false);
    const timeSlots = generateTimeSlots();
    const {plt} = useTheme();

    // Mobile detection
    useEffect(() => {
        const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIsMobile();
        window.addEventListener("resize", checkIsMobile);
        return () => window.removeEventListener("resize", checkIsMobile);
    }, []);

    // Escape key handler
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setSelection({firstClickedSlot: null, availableSlots: []});
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Find all free intervals in a time range
    const findFreeIntervals = (start: Date, end: Date): TimeInterval[] => {
        const relevantEvents = events
            .filter(event => {
                const eventStart = parseISO(event.start);
                const eventEnd = parseISO(event.end);
                return eventStart < end && eventEnd > start;
            })
            .sort((a, b) => parseISO(a.start).getTime() - parseISO(b.start).getTime());

        if (relevantEvents.length === 0) {
            return [{start, end}];
        }

        const freeIntervals: TimeInterval[] = [];
        let currentTime = start;

        relevantEvents.forEach(event => {
            const eventStart = parseISO(event.start);
            const eventEnd = parseISO(event.end);

            if (currentTime < eventStart) {
                freeIntervals.push({
                    start: currentTime,
                    end: eventStart
                });
            }

            currentTime = eventEnd > currentTime ? eventEnd : currentTime;
        });

        if (currentTime < end) {
            freeIntervals.push({
                start: currentTime,
                end
            });
        }

        return freeIntervals;
    };

    // Find available time slots for selection
    const findAvailableSlots = (clickedTime: Date): Date[] => {
        const maxDate = maxDaysForSelection === 0
            ? endOfDay(clickedTime)
            : addDays(clickedTime, maxDaysForSelection);
        const minDate = maxDaysForSelection === 0
            ? startOfDay(clickedTime)
            : addDays(clickedTime, -maxDaysForSelection);

        const availableSlots: Date[] = [];
        let currentHour = clickedTime;

        // Forward search
        while (isBefore(currentHour, maxDate) || isEqual(currentHour, maxDate)) {
            const freeIntervals = findFreeIntervals(
                currentHour,
                addHours(currentHour, 1)
            );

            if (freeIntervals.length > 0) {
                availableSlots.push(currentHour);
            } else {
                break;
            }
            currentHour = addHours(currentHour, 1);
        }

        // Backward search
        currentHour = addHours(clickedTime, -1);
        while (isAfter(currentHour, minDate) || isEqual(currentHour, minDate)) {
            const freeIntervals = findFreeIntervals(
                currentHour,
                addHours(currentHour, 1)
            );

            if (freeIntervals.length > 0) {
                availableSlots.unshift(currentHour);
            } else {
                break;
            }
            currentHour = addHours(currentHour, -1);
        }

        return availableSlots;
    };

    // Handle time slot click
    const handleTimeSlotClick = (clickedTime: Date) => {
        if (userType !== "psychologist" || !canEdit) return;

        if (!selection.firstClickedSlot) {
            const availableSlots = findAvailableSlots(clickedTime);
            setSelection({
                firstClickedSlot: clickedTime,
                availableSlots,
            });
        } else {
            // Check if the clicked time is in available slots
            if (isSlotAvailable(clickedTime)) {
                handleSecondClick(clickedTime);
            } else {
                // Cancel selection if clicked outside available slots
                setSelection({firstClickedSlot: null, availableSlots: []});
            }
        }
    };

    // Handle the second click in selection
    const handleSecondClick = (clickedTime: Date) => {
        const {firstClickedSlot} = selection;
        if (!firstClickedSlot) return;

        const earlierTime = isBefore(firstClickedSlot, clickedTime)
            ? firstClickedSlot
            : clickedTime;
        const laterTime = isAfter(clickedTime, firstClickedSlot)
            ? clickedTime
            : firstClickedSlot;


        // Find all free intervals in the selected range
        const freeIntervals = findFreeIntervals(earlierTime, addHours(laterTime, 1));

        if (freeIntervals.length === 0) {
            setSelection({firstClickedSlot: null, availableSlots: []});
            return;
        }

        // Create availability for each free interval
        freeIntervals.forEach(interval => {
            const utcStartTime = fromZonedTime(interval.start, timeZone);
            const utcEndTime = fromZonedTime(interval.end, timeZone);

            if (maxDaysForSelection === 0 || isSameDay(interval.start, interval.end)) {
                onCreateAvailability(utcStartTime, utcEndTime);
            }
        });

        setSelection({firstClickedSlot: null, availableSlots: []});
    };

    // Check if a slot is available for selection
    const isSlotAvailable = (slotTime: Date): boolean => {
        return selection.availableSlots.some(slot => isEqual(slot, slotTime));
    };

    return (
        <div className={"w-100 flex-grow-1 user-select-none overflow-x-auto"}>
            <Table stickyHeader sx={styles.table}>
                <TableHead>
                    <TableRow sx={styles.tableHeader}>
                        <TableCell align="center" className={"border-bottom-0 text-center py-1 px-0 px-sm-2"}>
                            <Typography variant="h6" className={"frcc m-0 p-0"}
                                        sx={{...styles.dayHeaderText, color: plt.text.primary70}}>
                                Время
                            </Typography>
                        </TableCell>
                        {Array.from({length: 7}).map((_, index) => {
                            const mondayStart = addDays(currentWeekStart, (1 - currentWeekStart.getDay() + 7) % 7);
                            const currentDate = addDays(mondayStart, index);
                            return (
                                <TableCell key={index} align="center"
                                           className={"border-bottom-0 text-center py-1 px-0 px-sm-2"}>
                                    <Typography variant="h6" className={"frcc m-0 p-0"}
                                                sx={{...styles.dayHeaderText, color: plt.text.primary70}}>
                                        {!isMobile && getRussianWeekdayAbbreviation(currentDate)}{" "}
                                        {format(currentDate, "dd.MM", {locale: ru})}
                                    </Typography>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {timeSlots.map((time, rowIndex) => (
                        <TableRow key={rowIndex} sx={styles.timeRow}>
                            <TableCell sx={{
                                ...styles.timeSlot,
                                borderBottom: rowIndex === timeSlots.length - 1 ? "1px solid #444" : "none"
                            }}>
                                <Typography variant="body2"
                                            sx={{...styles.timeSlotText, color: plt.text.primary70}}>
                                    {time}
                                </Typography>
                            </TableCell>
                            {Array.from({length: 7}).map((_, colIndex) => {
                                const currentDay = format(addDays(currentWeekStart, colIndex), "yyyy-MM-dd");
                                const timeSlotStart = parseISO(`${currentDay}T${time}:00`);

                                return (
                                    <TimeSlotCell
                                        key={colIndex}
                                        events={events}
                                        currentDay={currentDay}
                                        time={time}
                                        onEdit={onEdit}
                                        onAdd={onAdd}
                                        userType={userType}
                                        canEdit={canEdit}
                                        onRemove={onRemove}
                                        onCreateAvailability={onCreateAvailability}
                                        cellStyles={getCellStyles(currentDay, time, rowIndex === timeSlots.length - 1, events, showHorizontalLines)}
                                        onSlotClick={() => handleTimeSlotClick(timeSlotStart)}
                                        isFirstClicked={selection.firstClickedSlot ? isEqual(timeSlotStart, selection.firstClickedSlot) : false}
                                        isAvailableForSelection={isSlotAvailable(timeSlotStart)}
                                        onConfirmSlot={onConfirmSlot}
                                    />
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
